<template>
    <!-- *********************************************
    *               Agent Number Block               *
    ********************************************** -->
    <divider-generic dividerSize="medium" icon="person" :title="t('payments.unitel.step1.agentNumberDivider')" :applyBorder="false"/>
    <!-- Agent Number Input -->
    <input-generic
    id="agentNumber"
    :params="agentNumberParams"
    :inputValue="formData.agentNumber"
    @input="[emit('formDataChange', { field: 'agentNumber', inputValue: $event }), clearFormErrors('agentNumber')]"/>
    <!-- Search Agent Number Button -->
    <button-tertiary
    id="searchAgentNumberButton"
    buttonSize="small"
    :label="t('payments.unitel.step1.searchAgentNumberButtonText')"
    :disabled="props.numberSearched === props.formData.agentNumber"
    @clicked="formData.agentNumber === '' ? showMandatoryErrorsHandler() : emit('searchAgentNumber')"/>
    <!-- Agent Name -->
    <text-block-generic
    v-if="formData.agentName.length"
    id="agentName"
    :params="agentNameParams"/>
    <!-- *********************************************
    *              Origin Account Block              *
    ********************************************** -->
    <divider-generic v-if="formData.agentName.length" dividerSize="medium" icon="account-balance" :title="t('payments.unitel.step1.originAccountDivider')"/>
    <!-- Origin Account Dropdown -->
    <dropdown-account
    v-if="formData.agentName.length"
    id="originAccount"
    :params="originAccountParams"
    :options="accountsBalances"
    :selectedOption="formData.originAccount"
    @clicked="[
        emit('formDataChange', { field: 'originAccount', inputValue: $event }),
        emit('formDataChange', { field: 'currency', inputValue: accountsBalances.find(account => account.id === $event).currency }) ]"/>
    <!-- *********************************************
    *                  Amount Block                  *
    ********************************************** -->
    <divider-generic v-if="formData.agentName.length" dividerSize="medium" icon="payment-single" :title="t('payments.unitel.step1.amountDivider')"/>
    <!-- Amount Input -->
    <input-amount
    v-if="formData.agentName.length"
    id="amount"
    :params="amountParams"
    :inputValue="formData.amount"
    @input="[emit('formDataChange', { field: 'amount', inputValue: $event }), clearFormErrors('amount')]"
    @onBlur="amountValidation"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import InputAmount from '@/components/generic/inputs/Amount'

/* Vue */
import { ref, computed, watch, onMounted } from 'vue'

/* Assets */
import { NUMBERS_ONLY } from '@/assets/constants/masks'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'formErrorsCleared', 'resetShowMandatoryErrorsProp', 'searchAgentNumber', 'scrollTo'])

/* props received
@accountsBalances { Array }
@clearFormErrors { Boolean } - Prop to remove of form errors
@formData { Object } - object with info for our current form
@isMobile { Boolean }
@mandatoryList {Array} Array of mandatory keys corresponding with our form
@numberSearched { String }
@showMandatoryErrors {Boolean} Prop to trigger display of mandatory errors
*/
const props = defineProps({
    accountsBalances: Array,
    clearFormErrors: Boolean,
    formData: Object,
    isMobile: Boolean,
    mandatoryList: Array,
    numberSearched: String,
    showMandatoryErrors: Boolean
})

/* Data */
const agentNumberParams = ref({
    labelText: t('payments.unitel.step1.agentNumberLabel'),
    placeholder: t('payments.unitel.step1.agentNumberPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null,
    maxLength: 10,
    mask: NUMBERS_ONLY
})

const agentNameParams = computed(() => {
    return {
        label: t('payments.unitel.step1.agentNameLabel'),
        content: props.formData.agentName
    }
})

const originAccountParams = ref({
	labelText: t('payments.unitel.step1.originAccountLabel'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountParams = ref({
	labelText: t('payments.unitel.step1.amountLabel'),
	currency: props.formData.currency,
    mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountValidation = () => {
    if (props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0) {
        amountParams.value.errorState = true
        amountParams.value.errorMessage = t('invalidAmount')
    }
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	agentNumberParams: agentNumberParams.value,
    agentNameParams: agentNumberParams.value,
    originAccountParams: originAccountParams.value,
    amountParams: amountParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }

    if (props.numberSearched !== props.formData.agentNumber) {
        emit('scrollTo', 'searchAgentNumberButton')
        agentNumberParams.value.errorState = true
        agentNumberParams.value.errorMessage = t('payments.unitel.step1.agentNumberErrorMessage')
    }

    if (props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0) {
        emit('scrollTo', getElementOffsetTopPosition('amount'))
        amountValidation()
    }

    if (props.formData.agentNumber === '') {
        agentNumberParams.value.errorState = true
        agentNumberParams.value.errorMessage = t('mandatoryFieldError')
    }
}

/* Logic to give agent name not found error */
watch(() => props.formData.agentName, () => {
    if (props.formData.agentName === null) {
        agentNumberParams.value.errorState = true
        agentNumberParams.value.errorMessage = t('payments.unitel.step1.agentNumberErrorMessage')
    }
})

watch(() => props.numberSearched, () => {
    if (props.numberSearched === null) {
        agentNumberParams.value.errorState = true
        agentNumberParams.value.errorMessage = t('payments.unitel.step1.agentNumberErrorMessage')
    }
})

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

onMounted(() => {
    emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
</style>
